import type { PolicyClass } from "./policy.js";
import type { IncidentRunbookType } from "./incidents.js";
import { SharedPolicyClassValue, SharedWorkflowStateValue } from "./sharedValues.js";

export enum ToolProviderKind {
  GitHub = "github",
  Notification = "notification",
  Telephony = "telephony",
  StatusPage = "status_page",
  DnsCdnLoadBalancer = "dns_cdn_load_balancer",
  CloudVpcFirewall = "cloud_vps_firewall",
  Database = "database",
  Observability = "observability",
  Deploy = "deploy",
  SshBastion = "ssh_bastion",
  Worker = "worker",
  Llm = "llm",
  Hetzner = "hetzner",
  Cloudflare = "cloudflare",
  Caddy = "caddy",
  Systemd = "systemd",
  Docker = "docker",
  Postgres = "postgres",
  Redis = "redis",
  Prometheus = "prometheus",
  Grafana = "grafana",
  GitHubRunner = "github_runner",
  TelnyxFreeswitch = "telnyx_freeswitch",
  Stripe = "stripe",
  Resend = "resend",
  OpenAiAzure = "openai_azure",
  SafeBrowsing = "safe_browsing",
  Notifications = "notifications",
  Approval = "approval",
  CodeBridge = "code_bridge",
  Vault = "vault",
  BreakGlass = "break_glass"
}
export enum ToolActionRisk {
  SafeRead = SharedPolicyClassValue.SafeRead,
  CustomerNotification = "customer_notification",
  TelephonyFailover = "telephony_failover",
  NetworkChange = "network_change",
  ProviderAdmin = "provider_admin",
  SshAccess = "ssh_access",
  SecretLifecycle = "secret_lifecycle",
  DeployRollback = "deploy_rollback",
  ManualBreakGlass = "manual_break_glass"
}

export enum CredentialStatus {
  Pending = "pending",
  Active = "active",
  Revoked = "revoked",
  Rotating = "rotating",
  SuspectedLeak = "suspected_leak"
}

export enum SecretAccessDecision {
  Allowed = "allowed",
  ApprovalRequired = SharedWorkflowStateValue.ApprovalRequired,
  Denied = "denied",
  VaultUnavailable = "vault_unavailable"
}

export enum SecretAccessReasonCode {
  Allowed = "secret_access_allowed",
  ApprovalRequired = "secret_access_approval_required",
  Denied = "secret_access_denied",
  VaultUnavailable = "secret_access_vault_unavailable"
}

export enum ToolExecutionDecision {
  Allowed = "allowed",
  ApprovalRequired = SharedWorkflowStateValue.ApprovalRequired,
  Denied = "denied",
  CredentialUnavailable = "credential_unavailable"
}

export enum ToolActionReasonCode {
  RunbookUnknown = "tool_runbook_unknown",
  TenantDenied = "tool_tenant_denied",
  ActionNotAllowed = "tool_action_not_allowed",
  EnvironmentDenied = "tool_environment_denied",
  ArgumentDenied = "tool_argument_denied",
  IncidentScopeMismatch = "tool_incident_scope_mismatch",
  ActionApprovalRequired = "tool_action_approval_required",
  ActionAllowed = "tool_action_allowed",
  RunbookPreviewNotAllowed = "tool_runbook_preview_not_allowed",
  RunbookNotReady = "tool_runbook_not_ready",
  CredentialUnavailable = "tool_credential_unavailable"
}

export enum McpActionId {
  IncidentGetStatus = "incident.get_status",
  IncidentCollectEvidence = "incident.collect_evidence",
  RunbookList = "runbook.list",
  RunbookPreviewAction = "runbook.preview_action",
  RunbookExecuteBoundedAction = "runbook.execute_bounded_action",
  ApprovalRequest = "approval.request",
  ApprovalGetStatus = "approval.get_status",
  NotificationPreview = "notification.preview",
  NotificationSendTemplate = "notification.send_template",
  CredentialStatus = "credential.status",
  CredentialRotate = "credential.rotate",
  CredentialRevoke = "credential.revoke",
  SshRequestGrant = "ssh.request_grant",
  SshRevokeGrant = "ssh.revoke_grant",
  NetworkPreviewChange = "network.preview_change",
  NetworkApplyApprovedChange = "network.apply_approved_change",
  DeployPreviewRollback = "deploy.preview_rollback",
  DeployExecuteRollback = "deploy.execute_rollback",
  DeployVerifyRelease = "deploy.verify_release",
  IncidentCloseout = "incident.closeout"
}

export enum McpActionFamily {
  Incident = "incident",
  Runbook = "runbook",
  Approval = "approval",
  Notification = "notification",
  Verification = "verification"
}

export enum McpRuntimeKind {
  GateReconciliation = "gate_reconciliation"
}

export enum McpExecutionMode {
  ReadSafe = "read_safe",
  ApprovedWrapper = "approved_wrapper"
}

export enum McpPermissionClass {
  SafeRead = SharedPolicyClassValue.SafeRead,
  ProviderWrite = "provider_write",
  CustomerSend = "customer_send",
  Deploy = "deploy",
  Failover = "failover",
  CredentialAccess = "credential_access",
  GitPublication = "git_publication",
  Destructive = "destructive",
  BreakGlass = "break_glass",
  ApprovalAdmin = "approval_admin",
  CodeRemediation = "code_remediation",
  Blocked = SharedWorkflowStateValue.Blocked
}

export enum McpInputFieldType {
  String = "string",
  Uuid = "uuid",
  Timestamp = "timestamp",
  GraphEnvironment = "graph_environment"
}

export interface McpActionDefinition {
  readonly id: McpActionId;
  readonly displayName: string;
  readonly policyClass: PolicyClass | null;
  readonly requiresApproval: boolean;
  readonly allowedToolIds: readonly string[];
}

export interface McpInputSchemaField {
  readonly name: string;
  readonly fieldType: McpInputFieldType;
  readonly required: boolean;
  readonly redacted: boolean;
}

export interface McpRuntimeActionDefinition {
  readonly id: string;
  readonly actionRef: string;
  readonly family: McpActionFamily;
  readonly runtimeKind: McpRuntimeKind;
  readonly provider: ToolProviderKind;
  readonly providerOperation: string;
  readonly inputSchema: readonly McpInputSchemaField[];
  readonly permissionClass: McpPermissionClass;
  readonly executionMode: McpExecutionMode;
  readonly requiresApproval: boolean;
  readonly requiresIncidentScope: boolean;
  readonly requiresOperationHash: boolean;
  readonly requiresApprovalState: boolean;
  readonly timeoutMs: number;
  readonly maxOutputBytes: number;
  readonly requireRedaction: boolean;
  readonly rollbackRef: string;
  readonly verificationRef: string;
  readonly auditEventPrefix: string;
  readonly approvalRequirement: string;
}

export interface McpRuntimeRegistration {
  readonly registrationId: string;
  readonly version: string;
  readonly runtimeKind: McpRuntimeKind;
  readonly actions: readonly McpRuntimeActionDefinition[];
}

export interface ToolActionDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly risk: ToolActionRisk;
  readonly policyClass: PolicyClass;
  readonly commandTemplate: string | null;
  readonly providerOperation: string | null;
  readonly allowedArgumentKeys: readonly string[];
  readonly deniedArgumentPatterns: readonly string[];
  readonly requiresApproval: boolean;
  readonly timeoutMs: number;
  readonly maxOutputBytes: number;
  readonly rollbackActionId: string | null;
}

export interface ToolRunbookReadiness {
  readonly ready: boolean;
  readonly requiredEvidenceSources: readonly string[];
  readonly requiredPreparationChecks: readonly string[];
  readonly verificationActionIds: readonly string[];
  readonly containmentActionIds: readonly string[];
}

export interface ToolingReadinessRegistry {
  readonly checkedAt: string;
  readonly availableProviders: readonly ToolProviderKind[];
  readonly availableEvidenceSources: readonly string[];
}

export type ToolRunbookReadinessFailureCode =
  | "tool_runbook_provider_unavailable"
  | "tool_runbook_evidence_source_missing"
  | "tool_runbook_verification_action_missing"
  | "tool_runbook_containment_action_missing";

export type ToolingInfrastructureReadinessErrorCode = "tooling_infrastructure_not_ready";

export interface ToolRunbookReadinessFailure {
  readonly runbookId: string;
  readonly provider: ToolProviderKind;
  readonly code: ToolRunbookReadinessFailureCode;
}

export interface ToolingInfrastructureReadinessReport {
  readonly ready: boolean;
  readonly checkedAt: string;
  readonly availableProviders: readonly ToolProviderKind[];
  readonly availableEvidenceSources: readonly string[];
  readonly failures: readonly ToolRunbookReadinessFailure[];
}

export interface ToolRunbook {
  readonly id: string;
  readonly displayName: string;
  readonly owner: string;
  readonly tenantScope: string;
  readonly environmentScope: readonly string[];
  readonly provider: ToolProviderKind;
  readonly incidentTypes: readonly IncidentRunbookType[];
  readonly mcpActions: readonly McpActionId[];
  readonly actionDefinitions: readonly ToolActionDefinition[];
  readonly deniedActions: readonly string[];
  readonly requiredCredentialScopes: readonly string[];
  readonly credentialExpiresAfterSeconds: number | null;
  readonly redactionRules: readonly string[];
  readonly rateLimitPerMinute: number;
  readonly concurrencyLimit: number;
  readonly revocationProcedure: string;
  readonly auditEventPrefix: string;
  readonly readiness: ToolRunbookReadiness;
}

export interface PublicToolCatalogEntry {
  readonly id: string;
  readonly label: string;
  readonly provider: ToolProviderKind;
  readonly owner: string;
  readonly environmentScope: readonly string[];
  readonly actionCount: number;
  readonly requiresApproval: boolean;
  readonly ready: boolean;
}

export interface SecretReference {
  readonly id: string;
  readonly tenantId: string;
  readonly provider: ToolProviderKind;
  readonly scope: string;
  readonly fingerprint: string;
  readonly status: CredentialStatus;
  readonly expiresAt: string | null;
}

export interface ToolCredentialGrant {
  readonly id: string;
  readonly tenantId: string;
  readonly toolId: string;
  readonly secretReferenceId: string;
  readonly owner: string;
  readonly actionIds: readonly string[];
  readonly environment: string;
  readonly status: CredentialStatus;
  readonly createdAt: string;
  readonly expiresAt: string | null;
}

export interface SecretAccessRequest {
  readonly id: string;
  readonly tenantId: string;
  readonly actorId: string;
  readonly toolId: string;
  readonly secretReferenceId: string;
  readonly actionId: string;
  readonly reason: string;
  readonly requestedAt: string;
}

export interface SecretAccessResult {
  readonly decision: SecretAccessDecision;
  readonly approvalId: string | null;
  readonly reasonCode: SecretAccessReasonCode;
}

export interface SshAccessGrant {
  readonly id: string;
  readonly tenantId: string;
  readonly actorId: string;
  readonly hostAlias: string;
  readonly environment: string;
  readonly principal: string;
  readonly reason: string;
  readonly approved: boolean;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
}

export interface FirewallChangeRequest {
  readonly id: string;
  readonly tenantId: string;
  readonly actorId: string;
  readonly environment: string;
  readonly sourceCidr: string;
  readonly destinationAlias: string;
  readonly port: number;
  readonly expiresAt: string;
  readonly rollbackActionId: string | null;
  readonly approved: boolean;
}

export interface ToolExecutionRequest {
  readonly id: string;
  readonly tenantId: string;
  readonly actorId: string;
  readonly toolId: string;
  readonly actionId: string;
  readonly environment: string;
  readonly arguments: Readonly<Record<string, string>>;
  readonly incidentId: string | null;
  readonly requestedAt: string;
}

export interface ToolExecutionResult {
  readonly decision: ToolExecutionDecision;
  readonly approvalId: string | null;
  readonly operationHash: string;
  readonly policyClass: PolicyClass | null;
  readonly reasonCode: ToolActionReasonCode;
}

export interface ToolActionPreview {
  readonly runbookId: string;
  readonly actionId: string;
  readonly mcpActionId: McpActionId;
  readonly decision: ToolExecutionDecision;
  readonly policyClass: PolicyClass | null;
  readonly requiresApproval: boolean;
  readonly requiresCredentialForExecution: boolean;
  readonly operationHash: string;
  readonly providerOperation: string | null;
  readonly rollbackActionId: string | null;
  readonly revocationProcedure: string | null;
  readonly auditEventPrefix: string | null;
  readonly reasonCode: ToolActionReasonCode;
}

export interface LlmPromptBoundary {
  readonly allowedEvidenceKinds: readonly string[];
  readonly deniedPatterns: readonly string[];
  readonly maxPromptBytes: number;
  readonly requireRedaction: boolean;
}

export interface RedactedPrompt {
  readonly text: string;
  readonly redactionCategories: readonly string[];
  readonly blocked: boolean;
}

export const toolProviderKinds = Object.values(ToolProviderKind);
export const credentialStatuses = Object.values(CredentialStatus);
export const mcpActionIds = Object.values(McpActionId);
export const mcpActionFamilies = Object.values(McpActionFamily);
export const mcpRuntimeKinds = Object.values(McpRuntimeKind);
export const mcpExecutionModes = Object.values(McpExecutionMode);
export const mcpPermissionClasses = Object.values(McpPermissionClass);

export function isToolProviderKind(value: string | null | undefined): value is ToolProviderKind {
  return value !== null && value !== undefined && toolProviderKinds.some((kind) => kind === value);
}

export function isCredentialStatus(value: string | null | undefined): value is CredentialStatus {
  return value !== null && value !== undefined && credentialStatuses.some((status) => status === value);
}

export function isMcpActionId(value: string | null | undefined): value is McpActionId {
  return value !== null && value !== undefined && mcpActionIds.some((actionId) => actionId === value);
}

export function isMcpActionFamily(value: string | null | undefined): value is McpActionFamily {
  return value !== null && value !== undefined && mcpActionFamilies.some((family) => family === value);
}

export function isMcpRuntimeKind(value: string | null | undefined): value is McpRuntimeKind {
  return value !== null && value !== undefined && mcpRuntimeKinds.some((kind) => kind === value);
}

export function isMcpExecutionMode(value: string | null | undefined): value is McpExecutionMode {
  return value !== null && value !== undefined && mcpExecutionModes.some((mode) => mode === value);
}

export function isMcpPermissionClass(value: string | null | undefined): value is McpPermissionClass {
  return value !== null && value !== undefined && mcpPermissionClasses.some((permissionClass) => permissionClass === value);
}
